/**
 * The four interests inside a crown.
 *
 * Before this file a crown was a single will with a treasury: the only internal
 * machinery on the strategic layer was a court that moved when a capital fell.
 * Every decision a crown made was free of anybody at home having an opinion
 * about it, which is why a hundred months of campaign could be played without
 * once being told *no* by your own people.
 *
 * This is the **smallest true version** of the repair, and it is deliberately
 * not a court-intrigue system. There are no characters, no plots, no marriages
 * and no assassinations. There are four standing bodies, each with one number,
 * and each number owns exactly one lever on the board:
 *
 *   | Estate         | What moves it                    | What it costs or grants |
 *   |----------------|----------------------------------|-------------------------|
 *   | The Hosts      | fielding armies, winning wars    | **the levy**            |
 *   | The Temple     | faith, peace, a settled country  | **unrest**              |
 *   | Counting Houses| trade income, harbours, no wars  | **the tithe in gold**   |
 *   | Great Families | ground held, holdings built      | **garrison regrowth**   |
 *
 * One estate, one lever, so the readout is never a readout: a player who has
 * watched the Hosts fall for a decade can say what it is about to cost them
 * without reading a tooltip, and the sentence "trade for a decade and the
 * merchants love you and the hosts do not" is literally what the arithmetic
 * below does.
 *
 * ── Why a drift toward a target rather than a running tally ───────────────
 *
 * The same shape `diplomacy.ts` uses for opinion, and for the same reason. A
 * running tally of nudges is path-dependent in a way nobody can audit: it can
 * sit at −80 for two hundred months because of something that happened in year
 * three, and no amount of good behaviour is guaranteed to shift it. A drift
 * toward a target read off the *current board* means the standing always has a
 * reason you can point at — `estateReport` prints the terms — and it means the
 * system is self-correcting rather than a ratchet.
 *
 * Events push a standing directly, on top of the drift. That is the whole loop:
 * the board sets where an estate is heading, and the answers you give to the
 * realm's chronicle decide how fast you get there and where you are when it
 * matters. See `chronicle.ts`.
 *
 * Nothing here draws a random number. It is called once a month per crown, from
 * the council, and its cost is a walk of the province list — the same walk
 * `doIncome` already does.
 */

import { confessionHostility } from '../../data/confessions.ts';
import { nationDef } from '../../data/nations.ts';
import { clamp } from '../../core/math.ts';
import { hasCamp } from './bandits.ts';
import { rawPoints } from './armies.ts';
import { FOOD_BID_MONTHS, monthsOfFood } from './food.ts';
import { tradeIncomeOf } from './trade.ts';
import { holdingIncome } from './holdings.ts';
import { TreatyKind } from './diplotypes.ts';
import { UNREST_CORE, type CampaignState } from './types.ts';

/**
 * Who is in the room. Four, and four is the number for a reason: it is the
 * fewest that can disagree with each other in every direction that matters —
 * war against money, faith against ground — and few enough that a panel can
 * show all of them at once without a scrollbar.
 */
export const Estate = {
  /** The men who march, and the captains who own them. */
  Hosts: 0,
  /** The confession. Whatever this crown's faith happens to be. */
  Temple: 1,
  /** The counting houses, the factors, the wharf. */
  Merchants: 2,
  /** The landed. Whoever was here before the crown was. */
  Families: 3,
} as const;
export type EstateId = (typeof Estate)[keyof typeof Estate];

export const ESTATE_COUNT = 4;

export const ESTATE_NAMES: readonly string[] = [
  'The Hosts',
  'The Temple',
  'The Counting Houses',
  'The Great Families',
];

/** What each one is, in one line, for a panel that has room for one line. */
export const ESTATE_CREEDS: readonly string[] = [
  'Give us a war, or give us our arrears.',
  'The crown holds the ground. We hold the people standing on it.',
  'Peace is cheaper. We have done the arithmetic twice.',
  'We were here before your house and we will be here after it.',
];

/** A standing runs between these, like opinion, and for the same legibility. */
export const ESTATE_MIN = -100;
export const ESTATE_MAX = 100;

/**
 * Points a standing moves toward its target per month.
 *
 * Two and a half, which is forty months — a little over three years — to travel
 * the whole hundred points from indifference to devotion. That is the number the
 * design brief asked for in words: *trade for a decade and the merchants love
 * you.* A decade of consistent behaviour is exactly what it takes, and one good
 * year is not.
 */
export const ESTATE_DRIFT = 2.5;

/**
 * Below this the levy dries up entirely, and it is the one discontinuity in the
 * file.
 *
 * Every other effect here is a smooth multiplier, because a smooth multiplier is
 * what stops a system like this being a cliff the player falls off without
 * warning. This one is a cliff on purpose: the brief asked for *a refusal at the
 * worst moment*, and a levy that merely shrinks is not a refusal, it is a tax.
 *
 * ── Why −75 and not −60 ──────────────────────────────────────────────────
 *
 * Because **the board alone must never be able to call a strike.** The worst
 * target `hostsTarget` can return is −69: no host in the field (−35), a war going
 * badly (−22) and nothing covering the ground (−12). At −60 that was reachable by
 * simply *being unlucky*, and it was reached — measured at 42 per cent of all
 * months for an idle crown, which is not a dramatic refusal, it is a permanent
 * tax on having had a bad decade. Worse, it was close to a trap: no levy means no
 * men, and no men means no host, and no host is the term that put you there.
 *
 * At −75 the drift can carry a crown to −69 and no further, and the last six
 * points have to come from **answers the crown gave** — a regency taken, an envoy
 * bought off, a captain replaced, the men sent home for the harvest. So a strike
 * is always something you did, it is always recoverable (the drift pulls back up
 * at `ESTATE_DRIFT` a month the instant you stop doing it), and the manpower
 * already banked is untouched throughout, so a host can still be raised out of
 * the bank — which is the largest single term pushing the Hosts back up.
 *
 * Three points of slack rather than six, and that was measured too: at −75 the
 * strike fired in **zero** of sixteen hundred player-months, which is a lever
 * that does not exist. At −72 a crown already scraping the board floor is tipped
 * over by any one answer worth twelve points or more, and the drift climbs back
 * out in two or three months — a short, sharp refusal at a moment the player
 * built, which is the whole brief.
 *
 * `tests/realmevents.test.ts` holds the deck and the targets to that property.
 */
export const HOSTS_STRIKE = -72;

/**
 * The lowest `hostsTarget` can return from the board alone: −35 for fielding
 * nothing, −22 for a war going badly, −12 for covering no ground with it.
 *
 * Declared rather than left implicit because it is half of an invariant — the
 * other half being `HOSTS_STRIKE` — and an invariant split across two files with
 * nothing naming it is an invariant that gets broken by the next person to tune a
 * number. The test asserts the gap.
 */
export const HOSTS_BOARD_FLOOR = -69;

// ── The levers ─────────────────────────────────────────────────────────────
//
// Four readers, each returning a multiplier, each read by exactly one line
// elsewhere. They are functions rather than inlined arithmetic so that the
// forecast on the world map and the month that actually happens cannot disagree
// — the same failure `holdingIncome`'s own comment warns about.

/** Levy multiplier: 0 on strike, otherwise 0.5 … 1.5. */
export function estateLevyScale(c: CampaignState, nation: number): number {
  const s = standing(c, nation, Estate.Hosts);
  if (s <= HOSTS_STRIKE) return 0;
  return 1 + s / 200;
}

/** Tax multiplier from the counting houses: 0.6 … 1.4. */
export function estateGoldScale(c: CampaignState, nation: number): number {
  return 1 + standing(c, nation, Estate.Merchants) / 250;
}

/** How fast unrest settles, per the temple: 0.5 … 1.5. */
export function estateUnrestScale(c: CampaignState, nation: number): number {
  return 1 + standing(c, nation, Estate.Temple) / 200;
}

/** How fast a garrison refills, per the families: 0.6 … 1.4. */
export function estateGarrisonScale(c: CampaignState, nation: number): number {
  return 1 + standing(c, nation, Estate.Families) / 250;
}

/**
 * One estate's standing, safely.
 *
 * Total rather than throwing, because a save from before this file existed loads
 * with the array defaulted and every caller here is inside a monthly loop that
 * must not be able to take the realm down.
 */
export function standing(c: CampaignState, nation: number, estate: number): number {
  const n = c.nations[nation];
  if (!n) return 0;
  return n.estates?.[estate] ?? 0;
}

/** Move one estate. The only writer, so the clamp cannot be forgotten anywhere. */
export function nudgeEstate(
  c: CampaignState,
  nation: number,
  estate: number,
  delta: number,
): void {
  const n = c.nations[nation];
  if (!n || !n.estates) return;
  const v = clamp((n.estates[estate] ?? 0) + delta, ESTATE_MIN, ESTATE_MAX);
  // The same `-0` trap `openingOpinion` documents: `Math.round` of anything in
  // (-0.5, 0) is `-0`, which hashes differently from `0` and is written by
  // `JSON.stringify` as `0`. A standing that crossed zero downward would then
  // hash one way before a save and another way after it.
  const r = Math.round(v);
  n.estates[estate] = r === 0 ? 0 : r;
}

/** Fresh estates: everybody indifferent. */
export function freshEstates(): number[] {
  return [0, 0, 0, 0];
}

// ── Where each standing is heading ─────────────────────────────────────────

/**
 * One term of a target, kept as a name and a number so the panel can print the
 * *reason* rather than the total. An estate the player cannot argue with is a
 * readout; an estate that says "because you have no host in the field" is a
 * system.
 */
export interface EstateTerm {
  why: string;
  value: number;
}

export interface EstateReport {
  estate: number;
  standing: number;
  target: number;
  terms: EstateTerm[];
}

/** Board facts every target wants, gathered once per crown per month. */
interface Board {
  holdings: number;
  meanUnrest: number;
  points: number;
  wars: number;
  warScore: number;
  ports: number;
  campsOnUs: number;
  tierSteps: number;
  lost: number;
  tax: number;
  trade: number;
  sameFaithWar: boolean;
  sameFaithPeace: number;
  hostileAlly: boolean;
  /** Months the granaries cover at this month's bill. See `food.ts`. */
  larder: number;
}

function survey(c: CampaignState, n: number): Board {
  let holdings = 0;
  let unrest = 0;
  let ports = 0;
  let campsOnUs = 0;
  let tierSteps = 0;
  let lost = 0;
  let tax = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    // Ground somebody took off us. `claimedBy` is the memory of the last owner,
    // so this is exactly "provinces this crown has lost and not got back".
    if (prov.claimedBy === n && prov.owner !== n && prov.owner >= 0) lost++;
    if (prov.owner !== n) continue;
    holdings++;
    unrest += prov.unrest;
    if (prov.port > 0) ports++;
    tierSteps += Math.max(0, prov.tier - 1);
    if (hasCamp(c, p)) campsOnUs++;
    tax += holdingIncome(c, p).gold;
  }

  let points = 0;
  for (const a of c.armies) if (a.owner === n) points += rawPoints(a);

  let wars = 0;
  let warScore = 0;
  for (const war of c.diplo.wars) {
    const attacking = war.attackers.includes(n);
    const defending = war.defenders.includes(n);
    if (!attacking && !defending) continue;
    wars++;
    // `score` runs in the attacker's favour, so a defender reads it inverted.
    warScore += attacking ? war.score : -war.score;
  }

  // Faith. Two questions: are we shooting at anybody who prays as we do, and are
  // we in bed with anybody who does not.
  let sameFaithWar = false;
  let sameFaithPeace = 0;
  const mine = c.nations[n].confession;
  for (let o = 0; o < c.nations.length; o++) {
    if (o === n || !c.nations[o].alive) continue;
    if (confessionHostility(mine, c.nations[o].confession) !== 0) continue;
    if (c.nations[n].atWar[o] === 1) sameFaithWar = true;
    else sameFaithPeace++;
  }
  let hostileAlly = false;
  for (const t of c.diplo.treaties) {
    if (t.kind !== TreatyKind.Alliance) continue;
    const other = t.a === n ? t.b : t.b === n ? t.a : -1;
    if (other < 0 || !c.nations[other]?.alive) continue;
    if (confessionHostility(mine, c.nations[other].confession) !== 0) hostileAlly = true;
  }

  return {
    holdings,
    meanUnrest: holdings > 0 ? unrest / holdings : 0,
    points,
    wars,
    warScore,
    ports,
    campsOnUs,
    tierSteps,
    lost,
    tax,
    trade: tradeIncomeOf(c, n),
    sameFaithWar,
    sameFaithPeace,
    hostileAlly,
    larder: monthsOfFood(c, n),
  };
}

/**
 * The Hosts.
 *
 * Measured against the board rather than against an absolute: 34 armies stand on
 * a 191-province realm of 30 crowns in a typical month, so "does this crown
 * field anything at all" is the question with real variance in it, and an
 * absolute points threshold would have read the same for everybody.
 */
function hostsTarget(b: Board): EstateTerm[] {
  const t: EstateTerm[] = [];
  t.push(
    b.points > 0
      ? { why: 'a host stands in the field', value: 35 }
      : { why: 'the crown fields no host at all', value: -35 },
  );
  if (b.wars > 0) {
    t.push(
      b.warScore > 6
        ? { why: 'the war is going our way', value: 22 }
        : b.warScore < -6
          ? { why: 'the war is going badly', value: -22 }
          : { why: 'there is a war on, and it is a stalemate', value: 4 },
    );
  } else {
    t.push({ why: 'a long peace, and nothing for them to do', value: -14 });
  }
  // Points per province held. A crown with one host and twenty provinces is not
  // a crown with an army; it is a crown with an escort.
  const want = Math.max(1, b.holdings) * 240;
  t.push({
    why: 'the host is sized to the ground it has to cover',
    value: Math.round(-12 + 34 * clamp(b.points / want, 0, 1)),
  });
  return t;
}

function templeTarget(b: Board): EstateTerm[] {
  const t: EstateTerm[] = [];
  if (b.sameFaithWar) t.push({ why: 'we are at war with our own confession', value: -45 });
  else t.push({ why: 'no blood spilt inside the confession', value: 22 });
  t.push({
    why: 'crowns of our faith we are at peace with',
    value: Math.min(24, b.sameFaithPeace * 4),
  });
  if (b.hostileAlly) t.push({ why: 'we are allied to a hostile faith', value: -25 });
  t.push({
    why: 'the state of the country',
    value: Math.round(18 - 90 * clamp(b.meanUnrest, 0, 1)),
  });
  return t;
}

function merchantsTarget(b: Board): EstateTerm[] {
  const t: EstateTerm[] = [];
  // What share of this crown's money comes through them. Measured: 17 of 24
  // trade nodes have a leader holding over half, so this term genuinely
  // separates crowns rather than reading the same everywhere.
  const total = Math.max(1, b.tax + b.trade);
  t.push({
    why: 'the share of the realm’s money that comes through the wharf',
    value: Math.round(-10 + 70 * clamp(b.trade / total, 0, 1)),
  });
  t.push({
    why: b.ports > 0 ? 'harbours open for business' : 'not one harbour in the realm',
    value: b.ports > 0 ? Math.min(22, 10 + b.ports * 4) : -12,
  });
  if (b.wars > 0) t.push({ why: 'war is bad for business', value: -Math.min(35, b.wars * 14) });
  if (b.campsOnUs > 0)
    t.push({ why: 'brigands on the roads', value: -Math.min(28, b.campsOnUs * 12) });
  return t;
}

function familiesTarget(b: Board): EstateTerm[] {
  const t: EstateTerm[] = [];
  t.push({
    why: 'the state of the country',
    value: Math.round(12 - 80 * clamp(b.meanUnrest, 0, 1)),
  });
  t.push(
    b.holdings > UNREST_CORE
      ? { why: 'the realm is growing', value: Math.min(28, (b.holdings - UNREST_CORE) * 4) }
      : { why: 'the realm is small and getting no bigger', value: -10 },
  );
  // The ladder. Measured at 6.7 provinces above the first rung across the whole
  // board, so this is a small term almost everywhere — which is correct. It is
  // the term that rewards a crown that actually builds, and almost nobody does.
  if (b.tierSteps > 0)
    t.push({ why: 'holdings built up rather than merely held', value: Math.min(26, b.tierSteps * 5) });
  if (b.lost > 0) t.push({ why: 'ground lost and not got back', value: -Math.min(35, b.lost * 8) });
  /**
   * The larder, and it is the Great Families' business rather than anybody
   * else's: they are the people who own the fields the grain comes off, and a
   * crown that cannot feed the country is a crown failing at the one thing the
   * landed measure it by.
   *
   * A complaint and never a reward — nothing above `FOOD_BID_MONTHS` of stores
   * pushes the term at all, so a well-fed realm reads exactly as it did before
   * this existed rather than every crown on the board getting a standing bonus
   * to its garrisons. It reads the *stores* rather than this month's shortfall
   * because an estate target must be derivable from the board (see
   * `estateReport`), and because "the granaries are empty" is a judgement on a
   * decade of husbandry rather than on one bad harvest.
   *
   * Their lever is garrison regrowth, which is the right consequence: a hungry
   * country turns fewer men out for the watch.
   */
  const fed = clamp(b.larder / FOOD_BID_MONTHS, 0, 1);
  if (fed < 1) {
    t.push({
      why: b.larder <= 0.001 ? 'the granaries are empty' : 'there is barely a season of food in hand',
      value: Math.round(-30 * (1 - fed)),
    });
  }
  return t;
}

/**
 * Where a standing is heading and why, for the panel and for the tests.
 *
 * The terms are the whole point. An estate that only exposed a number would be
 * the readout the brief warned against; one that can be asked *why* is something
 * the player can decide to do something about.
 */
export function estateReport(c: CampaignState, nation: number, estate: number): EstateReport {
  const b = survey(c, nation);
  return {
    estate,
    standing: standing(c, nation, estate),
    target: targetOf(b, estate),
    terms: termsOf(b, estate),
  };
}

/** Every estate of one crown, in `Estate` order. One survey, not four. */
export function estateReports(c: CampaignState, nation: number): EstateReport[] {
  const b = survey(c, nation);
  const out: EstateReport[] = [];
  for (let e = 0; e < ESTATE_COUNT; e++) {
    out.push({
      estate: e,
      standing: standing(c, nation, e),
      target: targetOf(b, e),
      terms: termsOf(b, e),
    });
  }
  return out;
}

function termsOf(b: Board, estate: number): EstateTerm[] {
  switch (estate) {
    case Estate.Hosts:
      return hostsTarget(b);
    case Estate.Temple:
      return templeTarget(b);
    case Estate.Merchants:
      return merchantsTarget(b);
    default:
      return familiesTarget(b);
  }
}

function targetOf(b: Board, estate: number): number {
  let sum = 0;
  for (const t of termsOf(b, estate)) sum += t.value;
  return clamp(Math.round(sum), ESTATE_MIN, ESTATE_MAX);
}

/**
 * A month of estates, for every living crown.
 *
 * Called from the council, once, and it is the only thing in this file that
 * writes. Cost is one walk of the province list per crown — the same walk
 * `doIncome` does, and measured at well under a tenth of the month's work.
 */
export function estateTurn(c: CampaignState): void {
  for (let n = 0; n < c.nations.length; n++) {
    const nation = c.nations[n];
    if (!nation.alive) continue;
    if (!nation.estates) nation.estates = freshEstates();
    const b = survey(c, n);
    for (let e = 0; e < ESTATE_COUNT; e++) {
      const target = targetOf(b, e);
      const now = nation.estates[e] ?? 0;
      if (now === target) continue;
      const step = Math.min(ESTATE_DRIFT, Math.abs(target - now));
      nudgeEstate(c, n, e, now < target ? step : -step);
    }
  }
}

/**
 * The sentence a crown's estates amount to, for the world map's panel.
 *
 * A crown with nobody unhappy gets the dull line, which is the correct line:
 * most crowns most of the time have no internal politics worth reporting, and a
 * panel that manufactured drama for them would teach the player to stop reading
 * it.
 */
export function estateMood(c: CampaignState, nation: number): string {
  let worst = 0;
  let best = 0;
  for (let e = 1; e < ESTATE_COUNT; e++) {
    if (standing(c, nation, e) < standing(c, nation, worst)) worst = e;
    if (standing(c, nation, e) > standing(c, nation, best)) best = e;
  }
  const low = standing(c, nation, worst);
  const high = standing(c, nation, best);
  if (low <= HOSTS_STRIKE && worst === Estate.Hosts) {
    return `${ESTATE_NAMES[Estate.Hosts]} have stopped answering the muster.`;
  }
  if (low <= -45) return `${ESTATE_NAMES[worst]} will not be spoken to.`;
  if (low <= -20) return `${ESTATE_NAMES[worst]} are difficult.`;
  if (high >= 45) return `${ESTATE_NAMES[best]} would follow ${nationDef(c.nations[nation].def).name} anywhere.`;
  return 'Nobody at home is making trouble worth the name.';
}
